import { useState, useEffect } from "react";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import {
  Loader2,
  CheckCircle,
  Clock,
  DollarSign,
  FileText,
  Send,
  Check,
  X,
  AlertTriangle,
  Eye,
  MessageSquare,
  ShieldAlert
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

// Roles that can approve/reject invoices
const INVOICE_ADMIN_ROLES = ["super_admin", "admin_accountant"];

export default function BillingManagement() {
  const [invoices, setInvoices] = useState([]);
  const [customerInvoices, setCustomerInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("contractor");
  const { token, user } = useAuth();
  
  // Check if user can manage invoices
  const canManageInvoices = user && INVOICE_ADMIN_ROLES.includes(user.role);
  
  // Reject dialog state
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectingInvoice, setRejectingInvoice] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejecting, setRejecting] = useState(false);
  
  // View details dialog
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewingInvoice, setViewingInvoice] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [invRes, custRes] = await Promise.all([
        fetch(`${API}/admin/invoices`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/admin/customer-invoices`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      if (invRes.ok) setInvoices((await invRes.json()).invoices || []);
      if (custRes.ok) setCustomerInvoices((await custRes.json()).invoices || []);
    } catch (err) { toast.error("Failed to load invoices"); }
    finally { setLoading(false); }
  };

  const approveInvoice = async (id) => {
    try {
      const res = await fetch(`${API}/admin/invoices/${id}/approve`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) { toast.success("Invoice approved"); fetchData(); }
    } catch (err) { toast.error("Failed to approve"); }
  };

  const openRejectDialog = (invoice) => {
    setRejectingInvoice(invoice);
    setRejectReason("");
    setRejectDialogOpen(true);
  };

  const rejectInvoice = async () => {
    if (!rejectReason.trim()) {
      toast.error("Please provide a rejection reason");
      return;
    }
    setRejecting(true);
    try {
      const res = await fetch(`${API}/admin/invoices/${rejectingInvoice.id}/reject?reason=${encodeURIComponent(rejectReason)}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) { 
        toast.success("Invoice rejected"); 
        setRejectDialogOpen(false);
        setRejectingInvoice(null);
        setRejectReason("");
        fetchData(); 
      } else {
        toast.error("Failed to reject invoice");
      }
    } catch (err) { toast.error("Failed to reject"); }
    finally { setRejecting(false); }
  };

  const openViewDialog = (invoice) => {
    setViewingInvoice(invoice);
    setViewDialogOpen(true);
  };

  const sendCustomerInvoice = async (id) => {
    try {
      const res = await fetch(`${API}/admin/customer-invoices/${id}/send`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) { toast.success("Invoice sent to customer"); fetchData(); }
    } catch (err) { toast.error("Failed to send"); }
  };

  const markPaid = async (id) => {
    try {
      const res = await fetch(`${API}/admin/customer-invoices/${id}/mark-paid`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) { toast.success("Marked as paid"); fetchData(); }
    } catch (err) { toast.error("Failed to update"); }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;

  const pendingInvoices = invoices.filter(i => i.status === "pending");
  const rejectedInvoices = invoices.filter(i => i.status === "rejected");
  const pendingCustomer = customerInvoices.filter(i => i.status === "pending" || i.status === "sent");

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-slate-900">Billing Control Tower</h2>

      <div className="grid md:grid-cols-5 gap-4">
        <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-700">{pendingInvoices.length}</p>
              <p className="text-sm text-amber-600">Pending Approval</p>
            </div>
          </div>
        </div>
        <div className="bg-red-50 border border-red-100 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <X className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-red-700">{rejectedInvoices.length}</p>
              <p className="text-sm text-red-600">Rejected</p>
            </div>
          </div>
        </div>
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-700">{invoices.length}</p>
              <p className="text-sm text-blue-600">Total Invoices</p>
            </div>
          </div>
        </div>
        <div className="bg-purple-50 border border-purple-100 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Send className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-purple-700">{pendingCustomer.length}</p>
              <p className="text-sm text-purple-600">Customer Pending</p>
            </div>
          </div>
        </div>
        <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-emerald-700">
                ${invoices.filter(i => i.status === 'approved').reduce((sum, i) => sum + (i.amount || 0), 0).toLocaleString()}
              </p>
              <p className="text-sm text-emerald-600">Approved Total</p>
            </div>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="contractor">Contractor Invoices</TabsTrigger>
          <TabsTrigger value="customer">Customer Invoices</TabsTrigger>
        </TabsList>

        <TabsContent value="contractor" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Invoice</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Contractor</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Job</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {invoices.map(inv => (
                  <tr key={inv.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => openViewDialog(inv)}>
                    <td className="px-4 py-3 font-mono text-sm text-blue-600 hover:underline">#{inv.id?.slice(0,8)}</td>
                    <td className="px-4 py-3 text-sm">{inv.contractor_name || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{inv.ticket_id || inv.work_order_id || 'N/A'}</td>
                    <td className="px-4 py-3 font-medium">${inv.amount?.toLocaleString() || '0'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        inv.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                        inv.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                        inv.status === 'rejected' ? 'bg-red-100 text-red-700' :
                        'bg-slate-100 text-slate-600'
                      }`}>
                        {inv.status?.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => openViewDialog(inv)} 
                          className="h-8"
                          data-testid={`view-invoice-${inv.id?.slice(0,8)}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {inv.status === 'pending' && canManageInvoices && (
                          <>
                            <Button 
                              size="sm" 
                              onClick={() => approveInvoice(inv.id)} 
                              className="bg-emerald-600 hover:bg-emerald-700 h-8"
                              data-testid={`approve-invoice-${inv.id?.slice(0,8)}`}
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Approve
                            </Button>
                            <Button 
                              size="sm" 
                              variant="destructive"
                              onClick={() => openRejectDialog(inv)} 
                              className="h-8"
                              data-testid={`reject-invoice-${inv.id?.slice(0,8)}`}
                            >
                              <X className="w-4 h-4 mr-1" />
                              Reject
                            </Button>
                          </>
                        )}
                        {inv.status === 'pending' && !canManageInvoices && (
                          <span className="text-xs text-amber-600 flex items-center gap-1">
                            <ShieldAlert className="w-3 h-3" />
                            Awaiting Approval
                          </span>
                        )}
                        {inv.status === 'rejected' && inv.rejection_reason && (
                          <span className="text-xs text-red-600 flex items-center gap-1">
                            <MessageSquare className="w-3 h-3" />
                            {inv.rejection_reason.slice(0, 30)}...
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {invoices.length === 0 && (
              <div className="py-8 text-center text-slate-500">No invoices yet</div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="customer" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Invoice</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Customer</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {customerInvoices.map(inv => (
                  <tr key={inv.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => openViewDialog(inv)}>
                    <td className="px-4 py-3 font-mono text-sm text-blue-600 hover:underline">#{inv.id?.slice(0,8)}</td>
                    <td className="px-4 py-3 text-sm">{inv.customer_name || inv.hospital_name || 'N/A'}</td>
                    <td className="px-4 py-3 font-medium">${inv.amount?.toLocaleString() || '0'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        inv.status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                        inv.status === 'sent' ? 'bg-blue-100 text-blue-700' :
                        inv.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                        'bg-slate-100 text-slate-600'
                      }`}>
                        {inv.status?.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => openViewDialog(inv)} 
                          className="h-8"
                          data-testid={`view-customer-invoice-${inv.id?.slice(0,8)}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {inv.status === 'pending' && (
                          <Button size="sm" variant="outline" onClick={() => sendCustomerInvoice(inv.id)} className="h-8">
                            <Send className="w-4 h-4 mr-1" />
                            Send
                          </Button>
                        )}
                        {inv.status === 'sent' && (
                          <Button size="sm" onClick={() => markPaid(inv.id)} className="bg-emerald-600 hover:bg-emerald-700 h-8">
                            <Check className="w-4 h-4 mr-1" />
                            Paid
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {customerInvoices.length === 0 && (
              <div className="py-8 text-center text-slate-500">No customer invoices yet</div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Reject Invoice Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Reject Invoice
            </DialogTitle>
            <DialogDescription>
              Rejecting invoice #{rejectingInvoice?.id?.slice(0,8)} from {rejectingInvoice?.contractor_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-red-50 border border-red-100 rounded-lg p-3">
              <p className="text-sm text-red-700">
                <strong>Amount:</strong> ${rejectingInvoice?.amount?.toLocaleString()}
              </p>
              <p className="text-sm text-red-700">
                <strong>Job:</strong> {rejectingInvoice?.ticket_id || rejectingInvoice?.work_order_id}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="reject-reason">Rejection Reason *</Label>
              <Textarea
                id="reject-reason"
                placeholder="Please provide a reason for rejecting this invoice..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={4}
                data-testid="reject-reason-input"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={rejectInvoice}
              disabled={rejecting || !rejectReason.trim()}
              data-testid="confirm-reject-btn"
            >
              {rejecting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <X className="w-4 h-4 mr-2" />}
              Reject Invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Invoice Details Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Invoice Details
            </DialogTitle>
          </DialogHeader>
          {viewingInvoice && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-500 uppercase">Invoice ID</p>
                  <p className="font-mono text-sm">#{viewingInvoice.id?.slice(0,8)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Status</p>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    viewingInvoice.status === 'approved' || viewingInvoice.status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                    viewingInvoice.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                    viewingInvoice.status === 'sent' ? 'bg-blue-100 text-blue-700' :
                    viewingInvoice.status === 'rejected' ? 'bg-red-100 text-red-700' :
                    'bg-slate-100 text-slate-600'
                  }`}>
                    {viewingInvoice.status?.toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">
                    {viewingInvoice.contractor_name ? 'Contractor' : 'Customer'}
                  </p>
                  <p className="text-sm font-medium">
                    {viewingInvoice.contractor_name || viewingInvoice.customer_name || viewingInvoice.hospital_name || 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Amount</p>
                  <p className="text-lg font-bold text-emerald-600">${viewingInvoice.amount?.toLocaleString()}</p>
                </div>
                {(viewingInvoice.ticket_id || viewingInvoice.work_order_id || viewingInvoice.order_id) && (
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Reference</p>
                    <p className="font-mono text-sm">{viewingInvoice.ticket_id || viewingInvoice.work_order_id || viewingInvoice.order_id}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-slate-500 uppercase">Date</p>
                  <p className="text-sm">{viewingInvoice.created_at ? new Date(viewingInvoice.created_at).toLocaleDateString() : 'N/A'}</p>
                </div>
              </div>
              {viewingInvoice.description && (
                <div>
                  <p className="text-xs text-slate-500 uppercase mb-1">Description</p>
                  <p className="text-sm text-slate-700 bg-slate-50 p-3 rounded-lg">{viewingInvoice.description}</p>
                </div>
              )}
              {viewingInvoice.line_items && viewingInvoice.line_items.length > 0 && (
                <div>
                  <p className="text-xs text-slate-500 uppercase mb-2">Line Items</p>
                  <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                    {viewingInvoice.line_items.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span>{item.description || item.name}</span>
                        <span className="font-medium">${item.amount?.toLocaleString() || item.price?.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {viewingInvoice.status === 'rejected' && viewingInvoice.rejection_reason && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-xs text-red-500 uppercase mb-1">Rejection Reason</p>
                  <p className="text-sm text-red-700">{viewingInvoice.rejection_reason}</p>
                </div>
              )}
              {viewingInvoice.file_url && (
                <div>
                  <a 
                    href={viewingInvoice.file_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm"
                  >
                    <FileText className="w-4 h-4" />
                    View Invoice Document
                  </a>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>
              Close
            </Button>
            {viewingInvoice?.status === 'pending' && canManageInvoices && (
              <div className="flex gap-2">
                <Button 
                  onClick={() => { approveInvoice(viewingInvoice.id); setViewDialogOpen(false); }}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  <Check className="w-4 h-4 mr-1" />
                  Approve
                </Button>
                <Button 
                  variant="destructive"
                  onClick={() => { setViewDialogOpen(false); openRejectDialog(viewingInvoice); }}
                >
                  <X className="w-4 h-4 mr-1" />
                  Reject
                </Button>
              </div>
            )}
            {viewingInvoice?.status === 'pending' && !canManageInvoices && (
              <div className="flex items-center gap-2 text-amber-600 text-sm">
                <ShieldAlert className="w-4 h-4" />
                Only Super Admin or Admin Accountant can approve/reject
              </div>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
